import { isEmpty } from "lodash";

interface MediaUploadModel {
    Base64String: string;
    SaveImageAsType: string;
    AttachmentUrl: string;
}

export default class MediaUpload {
    private rootEl: HTMLDivElement
    private inputEl: HTMLInputElement
    private allowedItems: number
    private dropArea: HTMLDivElement;
    private uploadImageFromFiles: HTMLInputElement;
    private imageDisplayContainerElement: HTMLDivElement
    private closeButton: HTMLDivElement
    private stageElement: HTMLDivElement;
    private submitButtonContainer: HTMLDivElement;
    private submitButton: HTMLDivElement;
    private stagedFiles: File[] = [];
    constructor(rootElId: string, inputElId: string, allowedItems: string = "1") {
        this.rootEl = document.getElementById(rootElId) as HTMLDivElement
        this.inputEl = document.getElementById(inputElId) as HTMLInputElement;
        this.allowedItems = Number(allowedItems)
        this.dropArea = this.rootEl.querySelector<HTMLDivElement>('.media-drop-area');
        this.uploadImageFromFiles = this.rootEl.querySelector<HTMLInputElement>('#upload-from-files');
        this.imageDisplayContainerElement = this.rootEl.querySelector<HTMLDivElement>('.uploaded-file-area');
        this.closeButton = this.rootEl.querySelector<HTMLDivElement>('.close-modal');
        this.stageElement = this.rootEl.querySelector<HTMLDivElement>('.stage-element');
        this.submitButtonContainer = this.rootEl.querySelector<HTMLDivElement>('.submit-button');
        this.submitButton = this.submitButtonContainer.querySelector<HTMLDivElement>('.button');
    }

    Init() {
        ['dragenter', 'dragover', 'dragleave'].forEach(eventName => {
            this.dropArea.addEventListener(eventName, (e) => this.PreventDefaults(e));
        });

        this.closeButton.addEventListener("click", (e) => this.PreventDefaults(e));

        this.dropArea.addEventListener('drop', (e) => this.OnDrop(e));

        const deleteButtons = this.rootEl.querySelectorAll<HTMLDivElement>('.remove-button');
        deleteButtons?.forEach(button => {
            button.addEventListener('click', () => this.DeleteImage(button.parentElement));
        });

        this.uploadImageFromFiles.addEventListener('change', (e) => this.OnFileSelection(e));
        this.submitButton.addEventListener('click', async () => await this.SubmitUpload());
    }



    private PreventDefaults(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
    }

    private UpdateVisibility() {
        if (this.stagedFiles.length > 0) {
            this.submitButtonContainer.classList.remove('d-none');
            this.stageElement.style.minWidth = '180px';
        } else {
            this.submitButtonContainer.classList.add('d-none');
            this.stageElement.style.minWidth = '0';
        }
    }

    private OnDrop(e: DragEvent) {
        this.PreventDefaults(e)

        const dataTransfer = e.dataTransfer;
        const files = dataTransfer.files;

        this.HandleFiles(files);
    }

    private OnFileSelection(event: Event) {
        this.PreventDefaults(event);
        const input = event.target as HTMLInputElement;
        const files = input.files;


        if (files)
            this.HandleFiles(files);

        input.value = "";

    }

    private HandleFiles(files: FileList) {
        const addedImagesCount = this.rootEl.querySelector<HTMLDivElement>('.uploaded-file-area').childElementCount;

        if ((this.stagedFiles.length + files.length) > (this.allowedItems - addedImagesCount)) {
            alert(`You can only upload up to ${this.allowedItems} items.`);
            return;
        }

        for (let i = 0; i < files.length; i++) {
            if (files[i] && files[i] instanceof File && files[i].type.match('image.*'))
                this.DisplayFile(files[i]);
            else
                alert("Please upload an image file.");
        }

        this.UpdateVisibility();
    }


    private DisplayFile(file: File) {
        const fileName = file.name;

        const fileItem = document.createElement('div');
        fileItem.classList.add('file-element');
        fileItem.setAttribute('data-filename', fileName);

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const imgPreview = document.createElement('img');
                imgPreview.src = event.target.result as string;
                imgPreview.classList.add('image-preview');
                fileItem.appendChild(imgPreview);
            };
            reader.readAsDataURL(file);
        }

        const removeButton = document.createElement('div');
        removeButton.classList.add('remove-from-stage-button');
        removeButton.innerHTML = '<span class="dot-icon dot-stroke-close"></span>';
        removeButton.addEventListener('click', () => this.RemoveFile(fileItem));

        fileItem.appendChild(removeButton);
        this.stageElement.appendChild(fileItem);

        this.stagedFiles.push(file);
    }

    private async SubmitUpload() {
        // Show the spinner
        const spinner = this.rootEl.querySelector('#spinner');
        spinner.classList.remove('d-none');
        this.submitButtonContainer.classList.add('d-none');

        try {
            // Create an array of promises representing the upload operation for each file
            const uploadPromises = this.stagedFiles.map((file: File) => {
                return this.UploadFile(file);
            });

            // Wait for all uploads to complete
            await Promise.all(uploadPromises);

            // Clear the stage
            this.ClearStage();

            // Close the modal
            this.submitButtonContainer.closest<HTMLElement>('.media-upload-selector-modal').querySelector<HTMLButtonElement>('.close-modal')?.click();
        } catch (error) {
            console.error('Error uploading files:', error);
            // Handle error if necessary
        } finally {
            // Hide the spinner
            spinner.classList.add('d-none');
        }
    }

    private RemoveFile(fileItem: HTMLDivElement) {
        const indexToRemove = this.stagedFiles.findIndex(file => file.name === fileItem.getAttribute('data-filename'));
        if (indexToRemove !== -1)
            this.stagedFiles.splice(indexToRemove, 1);

        fileItem.remove();
        this.UpdateVisibility();
    }

    private ClearStage() {
        this.stagedFiles = [];
        this.stageElement.innerHTML = "";
        this.UpdateVisibility();
    }

    private async UploadFile(file: File) {
        return new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async () => {
                // Process the file upload
                try {
                    const base64String = reader.result?.toString().split(',')[1];
                    const postData: MediaUploadModel = {
                        Base64String: base64String,
                        SaveImageAsType: "MediaFileInfo",
                        AttachmentUrl: ""
                    };

                    const response = await fetch("/media/upload", {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(postData)
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to upload file: ${response.statusText}`);
                    }

                    const data = await response.json();
                    const fileUrl = data.imageUrl;

                    const inputValues = this.inputEl.value.split(',');

                    inputValues.push(fileUrl);


                    const checkedValues = inputValues.filter(val => !isEmpty(val));
                    this.inputEl.value = checkedValues.join(',');
                    // Update UI to display uploaded image
                    this.DisplayImage(fileUrl);
                    resolve()
                } catch (error) {
                    console.error('Error uploading file:', error);
                    reject(error)
                    // Handle error, if necessary
                }

            };
            reader.readAsDataURL(file);
        });

    }

    private DisplayImage(fileUrl: string) {
        const imgElement = document.createElement('img');
        imgElement.src = fileUrl;

        const deleteButton = document.createElement('div');
        deleteButton.classList.add('remove-button');
        deleteButton.innerHTML = '<span class="dot-icon dot-stroke-close"></span>';
        deleteButton.addEventListener('click', () => this.DeleteImage(deleteButton.parentElement));

        const container = document.createElement('div');
        container.appendChild(imgElement);
        container.appendChild(deleteButton);

        this.imageDisplayContainerElement.appendChild(container);
        this.UpdateContainerDisplay();
    }

    private DeleteImage(imgContainerElement: HTMLElement) {
        const imgElement = imgContainerElement.querySelector<HTMLImageElement>('img');
        if (imgElement) {
            const imageUrl = imgElement.src;
            const inputValues = this.inputEl.value.split(',');
            const updatedValues = inputValues.filter(val => val.trim() !== imageUrl);

            this.inputEl.value = updatedValues.join(',');
            //this.inputEl.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));

        }
        imgContainerElement.remove();
        this.UpdateContainerDisplay();
    }

    private UpdateContainerDisplay() {
        if (this.imageDisplayContainerElement.childElementCount == 0)
            this.imageDisplayContainerElement.classList.add('d-none');
        else
            this.imageDisplayContainerElement.classList.remove('d-none');
    }
}